import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, Trash, LockKey } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "staff" });

  const load = async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success("User created");
      setOpen(false);
      setForm({ email: "", name: "", password: "", role: "staff" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const toggleDisabled = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { disabled: !u.disabled });
      load();
    } catch { toast.error("Failed"); }
  };

  const resetPw = async (u) => {
    const pw = window.prompt(`New password for ${u.email}:`);
    if (!pw) return;
    try {
      await api.patch(`/users/${u.id}`, { password: pw });
      toast.success("Password updated");
    } catch { toast.error("Failed"); }
  };

  const del = async (u) => {
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-end">
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#de5d3e]">Access control</div>
          <h1 className="font-display font-black text-4xl md:text-5xl leading-none">Users.</h1>
        </div>
        <button onClick={() => setOpen(true)} className="brut-btn brut-btn-primary flex items-center gap-2" data-testid="add-user-btn">
          <Plus size={16} weight="bold" /> Add User
        </button>
      </div>

      <div className="brut-card p-0 overflow-hidden">
        <table className="brut" data-testid="users-table">
          <thead>
            <tr>
              <th>Name</th><th>Email</th><th>Role</th><th>Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td className="font-bold">{u.name}</td>
                <td className="font-mono-fin text-sm">{u.email}</td>
                <td><span className={`chip ${u.role === "admin" ? "chip-pos" : "chip-cash"}`}>{u.role}</span></td>
                <td>{u.disabled
                  ? <span className="chip chip-neg">Disabled</span>
                  : <span className="chip chip-pos">Active</span>}</td>
                <td>
                  <div className="flex gap-1 justify-end">
                    <button onClick={() => resetPw(u)} className="brut-btn brut-btn-secondary p-2" data-testid={`reset-pw-${u.id}`}>
                      <LockKey size={14} weight="bold" />
                    </button>
                    <button onClick={() => toggleDisabled(u)} className="brut-btn brut-btn-secondary text-xs" data-testid={`toggle-${u.id}`}>
                      {u.disabled ? "Enable" : "Disable"}
                    </button>
                    <button onClick={() => del(u)} className="brut-btn brut-btn-danger p-2" data-testid={`del-user-${u.id}`}>
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-[#1a1a1a]/60 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form onSubmit={create} onClick={(e) => e.stopPropagation()} className="brut-card-lg w-full max-w-md p-6" data-testid="user-modal">
            <div className="brut-label text-[#de5d3e] mb-1">New user</div>
            <h3 className="font-display font-black text-2xl mb-4">Add team member</h3>

            <label className="brut-label block mb-2">Full name</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="brut-input mb-4" data-testid="new-user-name" />

            <label className="brut-label block mb-2">Email</label>
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="brut-input mb-4" data-testid="new-user-email" />

            <label className="brut-label block mb-2">Password</label>
            <input required type="text" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="brut-input mb-4" data-testid="new-user-password" />

            <label className="brut-label block mb-2">Role</label>
            <div className="flex gap-2 mb-6">
              {["admin", "staff"].map((r) => (
                <button key={r} type="button" onClick={() => setForm({ ...form, role: r })}
                  className={`brut-btn flex-1 text-sm ${form.role === r ? "brut-btn-primary" : "brut-btn-secondary"}`} data-testid={`new-user-role-${r}`}>
                  {r.toUpperCase()}
                </button>
              ))}
            </div>

            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="brut-btn brut-btn-secondary">Cancel</button>
              <button type="submit" className="brut-btn brut-btn-primary" data-testid="create-user-submit">Create</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
